import { useState, type ReactNode } from 'react';
import { Button, Card, Badge, Input, Select, Modal, LoadingSkeleton } from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  useQuotes,
  useCreateQuote,
  useSendQuote,
  useAcceptQuote,
  useRejectQuote,
  useConvertQuoteToOrder,
  useDeleteQuote,
  type Quote,
  type QuoteInput,
} from './useQuotes';

const statusVariant: Record<string, 'success' | 'warning' | 'info' | 'error' | 'neutral'> = {
  DRAFT: 'neutral',
  SENT: 'info',
  ACCEPTED: 'success',
  EXPIRED: 'warning',
  REJECTED: 'error',
};

const statusLabel: Record<string, string> = {
  DRAFT: 'Rascunho',
  SENT: 'Enviado',
  ACCEPTED: 'Aceito',
  EXPIRED: 'Expirado',
  REJECTED: 'Rejeitado',
};

interface QuoteFormData {
  customerId: string;
  validityDays: number;
  conditions: string;
  items: {
    productId: string;
    description: string;
    quantity: number;
    unitValue: number;
  }[];
}

const emptyFormData: QuoteFormData = {
  customerId: '',
  validityDays: 30,
  conditions: '',
  items: [],
};

interface RejectDialogState {
  quoteId: string;
  reason: string;
}

export function QuotesPage(): JSX.Element {
  const { addToast } = useToast();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const pageSize = 20;

  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState<QuoteFormData>(emptyFormData);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [rejectDialog, setRejectDialog] = useState<RejectDialogState | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuotes({
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    page,
    limit: pageSize,
  });

  const createQuote = useCreateQuote();
  const sendQuote = useSendQuote();
  const acceptQuote = useAcceptQuote();
  const rejectQuote = useRejectQuote();
  const convertQuote = useConvertQuoteToOrder();
  const deleteQuote = useDeleteQuote();

  const total = data?.total ?? 0;
  const quotes = data?.data ?? [];
  const totalPages = Math.ceil(total / pageSize);

  const openCreateModal = (): void => {
    setFormData(emptyFormData);
    setFormErrors([]);
    setModalOpen(true);
  };

  const closeModal = (): void => {
    setModalOpen(false);
    setFormData(emptyFormData);
    setFormErrors([]);
  };

  const addItemRow = (): void => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { productId: '', description: '', quantity: 1, unitValue: 0 },
      ],
    }));
  };

  const updateItem = (
    index: number,
    field: keyof (typeof formData.items)[0],
    value: string | number,
  ): void => {
    setFormData((prev) => {
      const updated = [...prev.items];
      const item = { ...updated[index] } as (typeof formData.items)[0];
      if (!item) return prev;

      if (field === 'quantity') {
        item.quantity = typeof value === 'number' ? value : parseFloat(value) || 0;
      } else if (field === 'unitValue') {
        item.unitValue = typeof value === 'number' ? value : parseFloat(value) || 0;
      } else {
        (item as Record<string, unknown>)[field] = value;
      }

      updated[index] = item;
      return { ...prev, items: updated };
    });
  };

  const removeItem = (index: number): void => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];
    if (!formData.customerId.trim()) errors.push('Informe o ID do cliente');
    if (formData.items.length === 0) errors.push('Adicione pelo menos um item');
    if (formData.items.some((item) => item.quantity <= 0)) {
      errors.push('Quantidade dos itens deve ser maior que zero');
    }
    if (formData.validityDays <= 0) errors.push('Prazo de validade deve ser maior que zero');
    setFormErrors(errors);
    return errors.length === 0;
  };

  const handleSave = async (): Promise<void> => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const input: QuoteInput = {
        customerId: formData.customerId,
        validityDays: formData.validityDays,
        conditions: formData.conditions || undefined,
        items: formData.items.map((item) => ({
          productId: item.productId,
          description: item.description,
          quantity: item.quantity,
          unitValue: item.unitValue,
          discount: 0,
          discountType: 'FIXED',
          taxValues: {},
        })),
      };

      await createQuote.mutateAsync(input);
      addToast('Orçamento criado com sucesso', 'success');
      closeModal();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao criar orçamento';
      setFormErrors([message]);
      addToast(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSend = async (id: string): Promise<void> => {
    try {
      await sendQuote.mutateAsync(id);
      addToast('Orçamento enviado com sucesso', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao enviar orçamento';
      addToast(message, 'error');
    }
  };

  const handleAccept = async (id: string): Promise<void> => {
    try {
      await acceptQuote.mutateAsync(id);
      addToast('Orçamento aceito com sucesso', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao aceitar orçamento';
      addToast(message, 'error');
    }
  };

  const handleReject = async (): Promise<void> => {
    if (!rejectDialog) return;

    try {
      await rejectQuote.mutateAsync({
        id: rejectDialog.quoteId,
        reason: rejectDialog.reason,
      });
      addToast('Orçamento rejeitado', 'success');
      setRejectDialog(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao rejeitar orçamento';
      addToast(message, 'error');
    }
  };

  const handleConvert = async (id: string): Promise<void> => {
    try {
      const result = await convertQuote.mutateAsync(id);
      addToast(`Orçamento convertido em pedido ${result.orderId}`, 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao converter orçamento';
      addToast(message, 'error');
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    try {
      await deleteQuote.mutateAsync(id);
      addToast('Orçamento excluido com sucesso', 'success');
      setDeleteConfirmId(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao excluir orçamento';
      addToast(message, 'error');
    }
  };

  const renderActions = (quote: Quote): ReactNode => (
    <div className="action-buttons">
      {quote.status === 'DRAFT' && (
        <>
          <Button variant="primary" size="sm" onClick={() => handleSend(quote.id)}>
            Enviar
          </Button>
          {deleteConfirmId === quote.id ? (
            <div className="confirm-delete">
              <span>Tem certeza?</span>
              <Button variant="danger" size="sm" onClick={() => handleDelete(quote.id)}>
                Sim
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDeleteConfirmId(null)}>
                Não
              </Button>
            </div>
          ) : (
            <Button variant="danger" size="sm" onClick={() => setDeleteConfirmId(quote.id)}>
              Excluir
            </Button>
          )}
        </>
      )}
      {quote.status === 'SENT' && (
        <>
          <Button variant="primary" size="sm" onClick={() => handleAccept(quote.id)}>
            Aceitar
          </Button>
          <Button variant="danger" size="sm" onClick={() => setRejectDialog({ quoteId: quote.id, reason: '' })}>
            Rejeitar
          </Button>
        </>
      )}
      {quote.status === 'ACCEPTED' && (
        <Button variant="primary" size="sm" onClick={() => handleConvert(quote.id)}>
          Converter em pedido
        </Button>
      )}
    </div>
  );

  if (isLoading && !data) {
    return (
      <div className="quotes-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Orçamentos</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Orçamentos e conversão em pedido.</p>
          </div>
        </div>
        <Card>
          <LoadingSkeleton />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quotes-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Orçamentos</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Orçamentos e conversão em pedido.</p>
          </div>
        </div>
        <Card>
          <div className="error-state" role="alert">
            <p>Erro ao carregar orcamentos: {(error as Error).message}</p>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="quotes-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Orçamentos</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Orçamentos e conversão em pedido.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" onClick={openCreateModal}>
            Novo orcamento
          </Button>
        </div>
      </div>

      <Card>
        <div className="filters">
          <Select
            label=""
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="ALL">Todos os status</option>
            <option value="DRAFT">Rascunho</option>
            <option value="SENT">Enviado</option>
            <option value="ACCEPTED">Aceito</option>
            <option value="REJECTED">Rejeitado</option>
            <option value="EXPIRED">Expirado</option>
          </Select>
        </div>

        {quotes.length === 0 ? (
          <div className="empty-state">
            <p>Nenhum orçamento encontrado</p>
            <Button variant="primary" onClick={openCreateModal}>
              Criar primeiro orcamento
            </Button>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="quotes-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Data</th>
                    <th>Valido ate</th>
                    <th>Valor total</th>
                    <th>Status</th>
                    <th>Itens</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((quote) => (
                    <tr key={quote.id}>
                      <td>{quote.customerId}</td>
                      <td>{formatDate(quote.createdAt)}</td>
                      <td>{formatDate(quote.validUntil)}</td>
                      <td>{formatCurrency(quote.grandTotal)}</td>
                      <td>
                        <Badge variant={statusVariant[quote.status] ?? 'neutral'}>
                          {statusLabel[quote.status] ?? quote.status}
                        </Badge>
                      </td>
                      <td>{quote.items.length}</td>
                      <td>{renderActions(quote)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Anterior
                </button>
                <span className="page-info">
                  Pagina {page} de {totalPages} ({total} registros)
                </span>
                <button
                  className="page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Create Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title="Novo orçamento"
        size="lg"
        footer={
          <div className="modal-actions">
            <Button variant="outline" onClick={closeModal} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSave} loading={isSubmitting}>
              Criar orcamento
            </Button>
          </div>
        }
      >
        <div className="form-fields">
          <div className="form-row">
            <Input
              label="ID do cliente"
              value={formData.customerId}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, customerId: e.target.value }))
              }
              placeholder="UUID do cliente"
            />
            <Input
              label="Validade (dias)"
              type="number"
              value={String(formData.validityDays)}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  validityDays: parseInt(e.target.value, 10) || 0,
                }))
              }
              min="1"
            />
          </div>
          <div className="form-row">
            <Input
              label="Condicoes"
              value={formData.conditions}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, conditions: e.target.value }))
              }
              placeholder="Condicoes comerciais (opcional)"
            />
          </div>

          <div className="items-section">
            <div className="section-header">
              <h3>Itens</h3>
              <Button variant="outline" size="sm" onClick={addItemRow}>
                + Adicionar
              </Button>
            </div>

            {formData.items.length === 0 && (
              <p className="empty-items">
                Nenhum item adicionado. Clique em &quot;Adicionar&quot; para incluir.
              </p>
            )}

            {formData.items.map((item, index) => (
              <div key={index} className="item-row">
                <Input
                  label="ID do produto"
                  value={item.productId}
                  onChange={(e) => updateItem(index, 'productId', e.target.value)}
                  placeholder="UUID"
                />
                <Input
                  label="Descrição"
                  value={item.description}
                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                  placeholder="Descrição do item"
                />
                <Input
                  label="Qtd"
                  type="number"
                  value={String(item.quantity)}
                  onChange={(e) =>
                    updateItem(index, 'quantity', parseFloat(e.target.value) || 0)
                  }
                  min="0"
                  step="0.001"
                />
                <Input
                  label="Valor unit."
                  type="number"
                  value={String(item.unitValue)}
                  onChange={(e) =>
                    updateItem(index, 'unitValue', parseFloat(e.target.value) || 0)
                  }
                  min="0"
                  step="0.01"
                />
                <div className="item-total">
                  <label>Total</label>
                  <span>{formatCurrency(item.quantity * item.unitValue)}</span>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeItem(index)}
                >
                  Remover
                </Button>
              </div>
            ))}

            {formData.items.length > 0 && (
              <div className="quote-total">
                <strong>Total: </strong>
                <span>
                  {formatCurrency(
                    formData.items.reduce((sum, item) => sum + item.quantity * item.unitValue, 0),
                  )}
                </span>
              </div>
            )}
          </div>

          {formErrors.length > 0 && (
            <div className="form-errors" role="alert">
              {formErrors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Reject Dialog */}
      <Modal
        isOpen={!!rejectDialog}
        onClose={() => setRejectDialog(null)}
        title="Rejeitar orçamento"
        size="sm"
        footer={
          <div className="modal-actions">
            <Button variant="outline" onClick={() => setRejectDialog(null)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleReject}>
              Rejeitar
            </Button>
          </div>
        }
      >
        <div className="form-fields">
          <Input
            label="Motivo da rejeicao"
            value={rejectDialog?.reason ?? ''}
            onChange={(e) =>
              setRejectDialog((prev) =>
                prev ? { ...prev, reason: e.target.value } : null,
              )
            }
            placeholder="Descreva o motivo..."
          />
        </div>
      </Modal>

      <style>{`
        .quotes-page {
          padding: 1.5rem;
        }
        .filters {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .filters > *:first-child {
          flex: 1;
        }
        .table-wrapper {
          overflow-x: auto;
        }
        .quotes-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }
        .quotes-table th {
          text-align: left;
          padding: 0.75rem 1rem;
          background: #f2f4f6;
          color: #43474e;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5;
          white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .quotes-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #eceef0;
          color: #191c1e;
          white-space: nowrap;
        }
        .quotes-table tbody tr:hover td {
          background: #eceef0;
        }
        .action-buttons {
          display: flex;
          gap: 0.375rem;
          align-items: center;
          flex-wrap: wrap;
        }
        .confirm-delete {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.75rem;
          color: #ba1a1a;
        }
        .pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          padding: 1rem 0;
        }
        .page-btn {
          padding: 0.375rem 0.75rem;
          border: 1px solid #c4c6cf;
          background: #ffffff;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          font-family: inherit;
        }
        .page-btn:disabled {
          opacity: 0.5;
          cursor: default;
        }
        .page-info {
          font-size: 0.8125rem;
          color: #43474e;
        }
        .error-state {
          padding: 2rem;
          text-align: center;
          color: #ba1a1a;
        }
        .empty-state {
          padding: 3rem 2rem;
          text-align: center;
          color: #74777f;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }
        .modal-actions {
          display: flex;
          gap: 0.75rem;
        }
        .form-fields {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        .items-section {
          border-top: 1px solid #e0e3e5;
          padding-top: 1rem;
        }
        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 0.75rem;
        }
        .section-header h3 {
          margin: 0;
          font-size: 1rem;
          color: #191c1e;
        }
        .empty-items {
          text-align: center;
          color: #74777f;
          padding: 1rem 0;
          font-size: 0.875rem;
        }
        .item-row {
          display: grid;
          grid-template-columns: 1fr 1.5fr 80px 100px 100px 80px;
          gap: 0.5rem;
          align-items: end;
          padding: 0.5rem 0;
          border-bottom: 1px solid #eceef0;
        }
        .item-total label {
          display: block;
          font-size: 0.75rem;
          color: #43474e;
          margin-bottom: 0.25rem;
        }
        .item-total span {
          font-size: 0.875rem;
          font-weight: 600;
        }
        .quote-total {
          text-align: right;
          padding-top: 1rem;
          font-size: 1.125rem;
        }
        .form-errors {
          padding: 1rem;
          background: #ffdad6;
          border: 1px solid #ffb4ab;
          border-radius: 4px;
        }
        .form-errors p {
          color: #ba1a1a;
          font-size: 0.875rem;
          margin: 0.25rem 0;
        }
      `}</style>
    </div>
  );
}
